/* istanbul ignore file */
// FIXME: Add tests

import { isMigratedCustomPageSlug } from '@shared/utils/migratedCustomPages';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EventWebsiteGuestQuery } from '../../GuestSite.graphql.generated';
import type { EventWebsiteGuestMergedQuery } from '@apps/guest/shared/hooks/useEventWebsiteGuestData.types';
import { WebsitePageListDocument } from '@apps/guest/packages/layout-engine/layouts/hooks/websitehooks.graphql.generated';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { BrannanNavigation, DesignLayoutType, EventPageVisibility, EventUserStatus } from '@graphql/generated-base';
import type { EventType } from '@graphql/generated-base';
import { useImmer } from 'use-immer';
import { isInIframe } from '@shared/utils/isInIframe';
import { isGuestLockedOut } from '@shared/utils/isGuestLockedOut';
import { useAuth, useEventUserRole } from '@shared/components/AuthProvider';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useReloadForLegacyGuestsite, useGenerateGuestSiteLoadTelemData } from './GuestSite.utils';
import { useEventPageRouting } from './GuestSite.routes';
import type { OnPreviewListenerUpdate } from './PreviewListener';
import { useGuestSiteTelemetry, enrichTelemetryExtraInfo } from '../../GuestSite.telemetry';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useAppPage } from '@shared/utils/hooks/useAppPage';
import { useApolloClient } from '@apollo/client';
import { useGuestSiteHealth } from './GuestSite.health';
import { useSegmentGroupCallForEvent, getLocalStorage } from '@shared/core';
import { useComponentMountAndGraphqlQueryStatus } from '@shared/utils/hooks/useComponentMountAndGraphqlQueryStatus';
import { withWindow } from '@shared/utils/withWindow';
import { useIsMounted } from '@shared/utils/hooks/useIsMounted';
import { usePagesWithPhotoAlignment } from '@shared/customPage/hooks/usePagesWithPhotoAlignment';
import { useLayoutAndThemeOverride } from '@shared/hooks/useLayoutAndThemeOverride';
import { useEventPlaceCacheUpdate } from '@shared/utils/eventPlace';
import { useShouldHideRoomBlockPreview } from './hooks/useShouldHideRoomBlockPreview';
import type { GuestSiteArgs, ViewMode } from './GuestSite.types';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import { useAccommodationPageChecks } from '@apps/guest/hooks/useAccommodationPageChecks';
import { useNativeBrannanOverrideCheck } from '@apps/guest/hooks/useNativeBrannanOverrideCheck';
import { useGetGuestSitePages } from '@shared/hooks/useGetGuestSitePages';
import { useWebsitePublishCapabilityFeature } from '@shared/hooks/useWebsitePublishCapabilityFeature';
import { useEmailVerificationDialog } from '@shared/components/EmailVerificationDialog/useEmailVerificationDialog';
import { usePublishCrossSell } from '@shared/components/StationeryCrossSell';
import type { StationeryCrossSellTelemetrySurface } from '@shared/components/StationeryCrossSell';
import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { addError } from '@shared/utils/logger';
import { EventFeature, useProFeature } from '@shared/hooks/useProFeature';
import { pick } from 'lodash-es';
import { useAccommodationsVisibilityBanner } from './hooks/useAccommodationsVisibilityBanner';
import { useUnpublishedWebsiteBanner } from './hooks/useUnpublishedWebsiteBanner';
import { useAccommodationsVisibilityBannerDisplay } from './hooks/useAccommodationsVisibilityBannerDisplay';
import { useChangesReminderBanner } from './hooks/useChangesReminderBanner';
import { useInAppBrowserBanner } from './hooks/useInAppBrowserBanner';
import { useSmartAppBanner } from './hooks/useSmartAppBanner';
import type { MessageAdminEventPageChange } from '@shared/utils/previewMessageBus';
import { useEventWebsiteGuestData } from '@apps/guest/shared/hooks/useEventWebsiteGuestData';
import { useGuestSiteUnlockState } from '@apps/guest/shared/hooks/useGuestSiteUnlockState';
import { useTemplatePreviewStore } from './TemplatePreviewContext';
import { useIsGuestSiteSsrEnabled } from '@apps/guest/shared/hooks/useIsGuestSiteSsrEnabled';
import { deriveUnpublishedSiteGate } from './GuestSite.unpublishedGate';
import { useWebsiteV2GuestGate } from './useWebsiteV2GuestGate';
import { LazyCustom } from '@apps/guest/packages/layout-engine/widgets/Custom/Custom.lazy';
import { LazyCustomHoneymoon } from '@apps/guest/packages/layout-engine/widgets/Custom/components/Honeymoon/CustomHoneymoon.lazy';
import { LazyAnnouncement } from '@apps/guest/packages/layout-engine/components/Announcement/Announcement.lazy';
import { useIsStaysRedesignActive } from '@apps/guest/packages/layout-engine/widgets/Custom/components/Honeymoon/useIsStaysRedesignActive';
import { useIsNewStackEvent } from '@shared/utils/eventInfo/useIsNewStackEvent';
import { usesGeneralEventScheduleQuery } from '@shared/components/GeneralSchedule/usesFirebaseScheduleBackend';
import { useGoLiveChecksRedirect } from '@shared/components/GoLiveChecks/useGoLiveChecksRedirect';
// usePrefetchSsrQueries moved to PrefetchSsrQueriesRenderer in GuestSiteBody.tsx
// so it runs below PageContentVisibilityProvider — useSignalPageLoaded calls
// inside the prefetch's useFetch hooks can reach the provider's context.

const isPreviewing = isInIframe();

// Cross-sell events report against this page — matches the category the sibling
// PublishGoLive event on this surface already uses (websiteVisibilityPublish).
const CROSS_SELL_SURFACE: StationeryCrossSellTelemetrySurface = { category: 'adminWysiwyg', pagePrefix: 'admin', page: 'website' };

const ANNOUNCEMENT_DISMISSED_STORAGE_KEY_PREFIX = 'guest.announcementDismissed';
// localStorage (not session) so dismissal survives new tabs/visits — the fingerprint
// comparison in the announcement effect still re-shows the banner when the couple edits it.
const localStorage = getLocalStorage();
// Fingerprint covers title AND message: dismissal is persistent now, so editing
// either field must re-show the banner to guests who dismissed an older version.
const announcementFingerprint = (banner: { title: string; message: string }) => JSON.stringify([banner.title, banner.message]);
//Pages contained own additional lists of data.
const refetchPageQueries: Record<string, readonly string[]> = {
  schedule_wedding: ['GetGuestSiteSchedule'],
  schedule_general: ['GetProScheduleList'],
  travel: ['GetGuestsiteTravelList'],
  faq: ['GetGuestsiteFaq'],
  vip: ['GetGuestsiteVipsByHandle', 'GetGuestsiteVips', 'GetGuestsiteEventVips'],
  registry: ['GetGuestsiteRegistry'],
  accommodations: ['EventHotelReservedRoomBlocks', 'GuestEventHotelRoomBlocks', 'UserPublicConsentKeysForEvent', 'StayAreas'],
  welcome: ['BasicEventInfo', 'GetEventDetailsInfo'],
  custom: ['GeneralScheduleComponentScheduleForEvent', 'PlaceToStayAccommodations', 'GetEventDetailsInfo', 'GetGuestsiteEventVips'],
};

export type GuestSitePage = keyof typeof refetchPageQueries;

// Which schedule query a refetch has to target. Derived from the same predicate `useFetchScheduleListData`
// mounts its queries from, rather than restating the rule: refetching a query that is not mounted
// refreshes nothing, and the guest site keeps showing content the couple has already changed.
//
// It used to test `eventType === general` on its own, which sent a new-stack wedding's refetch to the
// wedding query while the pro one is what mounts. `usesGeneralEventScheduleQuery` rather than the
// negation of `usesFirebaseScheduleBackend`, because that negation is also true before the event type
// resolves — and until it does, the wedding query is the one mounted. Audit item P2.
export const resolveScheduleKey = (eventType: EventType | undefined | null, isNewStackEvent: boolean): string =>
  usesGeneralEventScheduleQuery(eventType, isNewStackEvent) ? 'schedule_general' : 'schedule_wedding';

export const useGuestSiteController = (args: GuestSiteArgs) => {
  const client = useApolloClient();
  const { onLoadComplete, onLoadFailure } = useGuestSiteHealth();
  const { isAdmin, hasIdentifiedUserOnce, role, isSuperAdmin, eventUserStatus } = useEventUserRole();
  const isNewStackEvent = useIsNewStackEvent();
  const { loginManager } = useAuth();
  // Capture token presence at mount (mirrors AuthProvider.tsx). Used to decide
  // whether to defer the unpublished-page placeholder until GetMe resolves.
  const hadTokenAtMount = useRef(loginManager.hasValidTokenForEagerAuth()).current;
  const { eventHandle, staticContext } = args;
  const [loadingTheme, setLoadingTheme] = useState(true);
  const [isPastFirstPage, setIsPastFirstPage] = useState(false);
  const addAppPage = useAppPage();
  const [hasEnrichedTelemetry, setHasEnrichedTelemetry] = useState(false);
  const { hideAccomPreviewUntilFlagEnabled } = useShouldHideRoomBlockPreview();
  const { toast } = useToast();
  const { tq } = useTranslation('publishReminder');
  const templatePreview = useTemplatePreviewStore();

  const queryParamHelper = useQueryParamHelper();
  const { currentPageSlug } = useCurrentPageSlug(eventHandle);
  const { isNativeAccommodationPage } = useAccommodationPageChecks(currentPageSlug);
  const { isNativeBrannanOverridePage } = useNativeBrannanOverrideCheck(currentPageSlug);

  // Honeymoon-redesign: preload BOTH lazy boundaries in parallel with EventWebsiteGuest (PRNT-4234).
  // - LazyCustom warms the outer router chunk (`content/guest/widgets/custom`).
  // - LazyCustomHoneymoon warms the inner honeymoon chunk (`guest/stays-page-honeymoon-custom`),
  //   which is where CustomHoneymoon.lazy.tsx's fallback would otherwise be visible during
  //   client-side SPA navigation if the inner chunk weren't preloaded.
  const isStaysRedesignActive = useIsStaysRedesignActive();
  useEffect(() => {
    if (isStaysRedesignActive) {
      LazyCustom.preload();
      LazyCustomHoneymoon.preload();
    }
  }, [isStaysRedesignActive]);

  const view = queryParamHelper.getValueString('view') as ViewMode;
  const isAnnouncementHidden = queryParamHelper.getValueString('hideannouncement') === 'true';

  const { executeSegmentGroupCallForEvent } = useSegmentGroupCallForEvent();

  //To define the previewed page on Admin
  const [currentAdminPage, setCurrentAdminPage] = useState(isPreviewing && currentPageSlug ? currentPageSlug : '');

  // FeatureFlag => wait for StyleApplicator https://github.com/joylifeinc/joy-web/pull/3214
  const styledApplicatorLoading = useFeatureValue('enableLoadingStyleApplicator');

  // Only fetch theme/font overrides for admin preview (not needed for regular guests)
  const { loadingThemeData, getEventWithThemeAndLayout, hasOverrides, loadingFontPackData } = useLayoutAndThemeOverride({
    ssr: true,
  });

  const websitePublishCapabilityFeature = useWebsitePublishCapabilityFeature();
  // WebsiteV2 events: charm only ever renders them UNPUBLISHED (a published site is proxied by
  // the server), so the gate is always armed and the charm `websiteState` is ignored — see
  // useWebsiteV2GuestGate.
  const websiteV2Gate = useWebsiteV2GuestGate();
  const isLoadingWebsitePublishCapability =
    websitePublishCapabilityFeature.eventExposure.isLoading || websitePublishCapabilityFeature.eventPublishedState.isLoading || websiteV2Gate.isLoading;
  const isWebsitePublishCapabilityEnabled = websiteV2Gate.isWebsiteV2 || websitePublishCapabilityFeature.eventExposure.value;

  const { data, loading, error, refetch, refetchPages } = useEventWebsiteGuestData({ eventHandle });
  // PLAN-17 step-2: unlock state comes from its own lean query, not EventWebsiteGuest.
  const { hasUnlocked, isUnlockStateLoading } = useGuestSiteUnlockState(eventHandle);

  // Telemetry data is now sourced from the main EventWebsiteGuest query (consolidated).
  const guestSiteLoadTelemData = data;
  const guestSiteLoadTelemDataLoading = loading;

  useComponentMountAndGraphqlQueryStatus({ loading, error, onComplete: () => onLoadComplete(), onError: error => onLoadFailure('Failed to load GuestSite', error) });

  executeSegmentGroupCallForEvent(data?.eventByName?.firebaseId, {
    postgresEventId: data?.eventByName?.id,
    eventType: data?.eventByName?.info.eventType,
  });

  const [{ eventPreview }, setEventPreviewData] = useImmer<{ eventPreview: EventWebsiteGuestMergedQuery | undefined }>(() => ({
    eventPreview: data,
  }));

  const [showBannerReminderChanges, setShowBannerReminderChanges] = useState(false);

  const isWebsitePublished = websiteV2Gate.isWebsiteV2 ? false : websitePublishCapabilityFeature.eventPublishedState.value;
  const isWebsitePublishedUpdating = websitePublishCapabilityFeature.eventPublishedState.isUpdating;
  const { ensureEmailVerified, dialogProps: emailVerificationDialogProps } = useEmailVerificationDialog();
  const { handlePublishSuccess, dialogProps: crossSellDialogProps } = usePublishCrossSell(CROSS_SELL_SURFACE);
  // SSR public-first: when enabled, remove hasIdentifiedUserOnce guard so unpublished gating
  // resolves immediately. During SSR, isAdmin is false (anonymous) so showUnpublishedPage is
  // true for unpublished events (safe fallback). After client-side auth resolves, isAdmin
  // flips → admin sees full site + banner.
  //
  // When disabled: preserve original behavior — wait for auth before deciding.
  const isGuestSiteSsrEnabled = useIsGuestSiteSsrEnabled();
  const authResolved = isGuestSiteSsrEnabled || hasIdentifiedUserOnce;
  const showUnpublishedBanner = authResolved && !isPreviewing && isAdmin && isWebsitePublishCapabilityEnabled && !isWebsitePublished;
  // PRNT-4313: token-bearing visitors must wait for GetMe to resolve before the
  // placeholder decides; otherwise admins see a brief flash of the unpublished
  // placeholder before their identity is known.
  const charmUnpublishedGate = deriveUnpublishedSiteGate({
    hasIdentifiedUserOnce,
    hadTokenAtMount,
    isAdmin,
    isGuestSiteSsrEnabled,
    isPreviewing,
    isWebsitePublishCapabilityEnabled,
    isWebsitePublished,
  });
  // WebsiteV2: the placeholder shows to EVERYONE, admins included — charm holds the couple's
  // abandoned V1 content, not their V2 draft, so there is nothing truthful for it to render. No
  // auth wait either: identity changes nothing here.
  const isWebsiteV2UnpublishedHardStop = websiteV2Gate.isWebsiteV2 && !isPreviewing;
  const showUnpublishedPage = isWebsiteV2UnpublishedHardStop || charmUnpublishedGate.showUnpublishedPage;
  const suppressGuestSiteUntilAuth = !isWebsiteV2UnpublishedHardStop && charmUnpublishedGate.suppressGuestSiteUntilAuth;

  const { showAccommodationsVisibilityBanner, handleCloseAccommodationsVisibilityBanner } = useAccommodationsVisibilityBanner({
    hasIdentifiedUserOnce,
    isAdmin,
    isPreviewing,
    isWebsitePublishCapabilityEnabled,
    isWebsitePublished,
  });

  const eventResponse = useMemo(() => {
    if (!data) return undefined;
    return hasOverrides ? getEventWithThemeAndLayout(data) : data;
  }, [data, hasOverrides, getEventWithThemeAndLayout]);

  // If previewing, use the clone. Else, use the original.
  const event = useMemo(() => {
    const whitelistedFields = ['featureFlags'];

    if (isPreviewing) {
      return eventPreview?.eventByName
        ? {
            ...eventPreview.eventByName,
            ...pick(eventPreview.eventByName || {}, whitelistedFields),
          }
        : undefined;
    }

    return eventResponse?.eventByName;
  }, [eventPreview?.eventByName, eventResponse?.eventByName]);

  const layout = isNativeBrannanOverridePage ? DesignLayoutType.brannan : event?.eventDesign?.websiteLayout.layoutType || DesignLayoutType.aloha;
  const hasUnpublishedChanges = event?.eventDesign?.hasUnpublishedChanges;

  if (staticContext) {
    staticContext.guestSiteEventHandleToVerify = eventHandle;
  }

  const { pages = [] } = event || {};

  const eventPageRoutes = useEventPageRouting(eventHandle, pages);

  const telemetry = useGuestSiteTelemetry();
  const updateContext = telemetry.updateContext;

  useEffect(() => {
    if (event && layout) updateContext(prev => ({ ...prev, eventId: event.id, layout }));
  }, [event, layout, updateContext]);

  const extraInfo = useGenerateGuestSiteLoadTelemData(guestSiteLoadTelemData?.eventByName, role, hasUnlocked);

  // Enrich all page view events with GuestSiteLoad properties to eliminate redundant GuestSiteLoad track event
  // No race condition: guestSiteLoadTelemDataLoading is included in the loading state,
  // so layouts won't render and page tracking won't start until enrichment completes
  useEffect(() => {
    // Also wait for the unlock query: enrichment runs once, and hasUnlocked feeds
    // security.isUnlocked — enriching while it's undefined would drop that field.
    if (!guestSiteLoadTelemDataLoading && !isUnlockStateLoading && guestSiteLoadTelemData && !hasEnrichedTelemetry) {
      enrichTelemetryExtraInfo({ ...extraInfo, guestSiteLoad: true });
      setHasEnrichedTelemetry(true);
    }
  }, [guestSiteLoadTelemDataLoading, isUnlockStateLoading, guestSiteLoadTelemData, setHasEnrichedTelemetry, extraInfo, hasEnrichedTelemetry]);

  // Preview

  // Seed the preview clone with server data until PreviewListener takes over.
  // Once PreviewListener processes its first design message, it owns eventPreview
  // and all subsequent updates arrive via postMessage — not from Apollo cache.
  // Without this guard, any Apollo cache write (e.g. the header fragment sync)
  // would re-emit eventResponse with stale template data and overwrite the preview.
  useEffect(() => {
    if (isPreviewing && !templatePreview.getHasPreviewListenerTakenOver()) {
      setEventPreviewData(draft => {
        draft.eventPreview = eventResponse;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setEventPreviewData, eventResponse]);

  // Respond to event preview updates if previewing
  const handleOnPreviewUpdate = useEventCallback<OnPreviewListenerUpdate>(cb => {
    if (isPreviewing) {
      setEventPreviewData(draft => {
        const eventDraft = draft.eventPreview?.eventByName;
        if (eventDraft) {
          cb(eventDraft, setCurrentAdminPage);
        }
      });
    }
  });

  //Refetch Preview data
  const { updateEventPlaceCache } = useEventPlaceCacheUpdate();
  // refetchPreviewLoading was removed: it was not the root cause of the JoyLogoLoader flash.
  // The actual cause was BasicEventInfo refetch → EventInfoProvider loading=true (cache-and-network
  // nextFetchPolicy) → useWebsitePublishCapabilityFeature → GuestSiteFeatureSet gate. Fixed by
  // stabilizing EventInfoProvider: `loading: !eventData && loading`.

  // After an explicit admin-triggered refetch, push the fresh server data into eventPreview.
  // This is separate from the init-only eventResponse sync — once PreviewListener takes over,
  // only this path and PreviewListener itself should write to eventPreview.
  // Refetch returns the raw EventWebsiteGuest result (no pages/pageContainers after PLAN-16 §7).
  // Read the freshly-refetched WebsitePageList directly from the Apollo cache rather than from
  // `data` in the React closure. When the `Promise.all([refetchQueries, refetchPromise])` settles,
  // Apollo has already written both responses to cache, but React's batched state updates for
  // `useWebsitePageListQuery` haven't flushed yet — `data` would still hold the pre-refetch
  // pages. Reading via `client.readQuery` sidesteps the React batching gap.
  const syncRefetchedDataToPreview = useEventCallback((freshData: EventWebsiteGuestQuery | undefined) => {
    if (!freshData) return;
    const cachedPageList = client.readQuery({
      query: WebsitePageListDocument,
      variables: { eventHandle, admin: isPreviewing && isAdmin },
    });
    const merged: EventWebsiteGuestMergedQuery = {
      ...freshData,
      eventByName: freshData.eventByName
        ? {
            ...freshData.eventByName,
            pages: cachedPageList?.eventByName?.pages ?? data?.eventByName?.pages ?? [],
            pageContainers: cachedPageList?.eventByName?.pageContainers ?? data?.eventByName?.pageContainers ?? [],
          }
        : freshData.eventByName,
    };
    const freshResponse = hasOverrides ? getEventWithThemeAndLayout(merged) : merged;
    setEventPreviewData(draft => {
      draft.eventPreview = freshResponse;
    });
  });

  const handleOnRefetchData = useEventCallback((message: MessageAdminEventPageChange) => {
    if (message.updateEventPlaceCache) {
      updateEventPlaceCache(message.updateEventPlaceCache);
      return;
    }
    // BasicEventInfo is always included in the refetch list so downstream providers stay fresh.
    // Previously this caused a JoyLogoLoader flash because refetching BasicEventInfo set
    // loading=true in EventInfoProvider (nextFetchPolicy: cache-and-network), which propagated
    // through useWebsitePublishCapabilityFeature → GuestSiteFeatureSet. Fixed by stabilizing
    // EventInfoProvider: `loading: !eventData && loading` — background refetches no longer
    // flip the loading flag once initial data has resolved.

    // message.value can be either a EventPageType type OR a custom page slug (for migrated pages)
    // Check if message.value is a valid key in refetchPageQueries
    // Schedule has separate query sets per event type — resolve the right key.
    const eventType = data?.eventByName?.info?.eventType;
    const lookupKey = message.value === 'schedule' ? resolveScheduleKey(eventType, isNewStackEvent) : message.value;

    const isValidGuestSitePage = lookupKey in refetchPageQueries;
    let query = isValidGuestSitePage ? refetchPageQueries[lookupKey] : undefined;
    // Check if the page is migrated - migrated pages should refetch immediately without delay
    // Note: message.value can be either a EventPageType type OR a custom page slug for migrated pages
    const isPageMigrated = typeof message.value === 'string' && isMigratedCustomPageSlug(message.value);
    // Migrated custom pages use GuestPageContainerBySlug (via useCustomData) to fetch component lists.
    // If the page is migrated but not in refetchPageQueries, use the 'custom' query set.
    if (isPageMigrated && !isValidGuestSitePage) {
      query = refetchPageQueries.custom;
    }

    const shouldRefetchImmediately = message.value === 'custom' || isPageMigrated;

    const executeRefetch = () => {
      const refetchPromise = refetch().then(result => result.data);

      // Evict cached pageContainerBySlug entries so that any mounted
      // GuestPageContainerBySlug query (via useCustomData) sees a cache miss
      // and refetches with its own valid variables. This replaces the previous
      // string-based refetchQueries approach which bypassed skip guards on
      // ObservableQuery instances, causing ~30K daily "pageSlug is required"
      // errors on event_service. See #14210 for the full writeup.
      client.cache.evict({ fieldName: 'pageContainerBySlug' });
      client.cache.gc();

      // refetchPages is the active WebsitePageList observer's own refetch — avoids the
      // `client.refetchQueries({ include: ['WebsitePageList'] })` pattern which would
      // wake the skip-guarded prefetch instance in usePrefetchSsrQueries. Awaiting it in
      // the same Promise.all keeps sync's freshness intact (pages live in WebsitePageList
      // after PLAN-16 §7; EWG `refetch()` no longer carries them).
      Promise.all([client.refetchQueries({ include: query ? [...query, 'BasicEventInfo'] : ['BasicEventInfo'] }), refetchPromise, refetchPages()])
        .then(([, freshData]) => {
          if (isPreviewing) {
            syncRefetchedDataToPreview(freshData);
          }
        })
        .catch(error => addError(error, { context: 'GuestSite', operation: 'refetchPreview' }));
    };

    if (!shouldRefetchImmediately) {
      //Wait until the new world DB is triggered to update
      //Timeout should be removed when all Admin edit pages transferred to JoyWeb
      setTimeout(executeRefetch, 2000);
    } else {
      executeRefetch();
    }
  });

  // Show/Hide BannerUnPublishedChanges
  useEffect(() => {
    if (!event || isPreviewing || view === 'draft') {
      return;
    }

    if (![EventUserStatus.admin, EventUserStatus.owner].includes(eventUserStatus)) {
      return;
    }

    if (hasUnpublishedChanges && !isWebsitePublishCapabilityEnabled) {
      setShowBannerReminderChanges(true);
      telemetry.notPublishedBanner('Shown');
    } else {
      setShowBannerReminderChanges(false);
    }
  }, [event, view, isAdmin, isSuperAdmin, eventUserStatus, hasUnpublishedChanges, telemetry, isWebsitePublishCapabilityEnabled]);

  const onDismissReminder = useCallback(() => {
    setShowBannerReminderChanges(false);
    telemetry.notPublishedBanner('Dismissed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickReminder = useCallback(() => {
    telemetry.notPublishedBanner('Published');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Announcement

  // Derived during render rather than in an effect, so the announcement is part of the first
  // commit that can show it instead of arriving a render later. `isMounted` keeps it out of the
  // SSR and hydration renders: the dismissal fingerprint lives in localStorage, which the server
  // can't read, so deciding before mount would disagree with the server's markup.
  const isAnnouncementMounted = useIsMounted();
  const announcementBanner = event?.info?.announcementBanner;
  const showAnnouncement = useMemo(() => {
    if (!isAnnouncementMounted || !announcementBanner?.active || isPreviewing || isAnnouncementHidden || !event?.id) {
      return false;
    }

    const comesFromRegistry = withWindow(({ document }) => !!document?.referrer?.endsWith('/registry'), false);
    if (comesFromRegistry) {
      return false;
    }

    // Show it unless it has been dismissed, or unless the content changed since that dismissal
    // (couple updated it).
    const dismissedFingerprint = localStorage.getItem(`${ANNOUNCEMENT_DISMISSED_STORAGE_KEY_PREFIX}.${event.id}`);
    return dismissedFingerprint !== announcementFingerprint(announcementBanner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnnouncementMounted, event?.id, announcementBanner?.active, announcementBanner?.title, announcementBanner?.message, isPreviewing, isAnnouncementHidden]);

  // Warm `content/guest/announcement` as soon as the event is known to have an active banner.
  // Without this the chunk fetch starts only once the gate opens, which is the remaining source
  // of visible delay now that the open-on-a-timer behaviour is gone.
  useEffect(() => {
    if (announcementBanner?.active) {
      LazyAnnouncement.preload();
    }
  }, [announcementBanner?.active]);

  // Re-fires when the couple edits the banner, matching the fingerprint that re-shows it.
  const shownAnnouncementFingerprintRef = useRef<string | null>(null);
  useEffect(() => {
    if (!showAnnouncement || !announcementBanner) {
      return;
    }
    const fingerprint = announcementFingerprint(announcementBanner);
    if (shownAnnouncementFingerprintRef.current === fingerprint) {
      return;
    }
    shownAnnouncementFingerprintRef.current = fingerprint;
    telemetry.announcementBannerChange(announcementBanner.title, 'Shown');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAnnouncement, announcementBanner?.title, announcementBanner?.message]);

  const onAnnouncementDismiss = useCallback(
    (title: string) => {
      if (event?.id && event.info?.announcementBanner) {
        const storageKey = `${ANNOUNCEMENT_DISMISSED_STORAGE_KEY_PREFIX}.${event.id}`;
        localStorage.setItem(storageKey, announcementFingerprint(event.info.announcementBanner));
      }
      telemetry.announcementBannerChange(title, 'Dismissed');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [event?.id, event?.info?.announcementBanner],
  );

  // Couples previewing their own unpublished site are asked with the Go Live Checks intro, whose
  // "Check My Site" leads to the editor's wizard. Skipped for everyone else, guests included. Only
  // the flag check lives here: the intro and its publish hook are a lazy host the body mounts on
  // demand (`GoLiveChecksIntroHost`), so their translations stay out of every guest's store.
  const { isRedirectEnabled: isGoLiveChecksOnSurface, isRedirectLoading: isGoLiveChecksLoading } = useGoLiveChecksRedirect({
    source: 'guestSiteBanner',
    skip: !showUnpublishedBanner,
  });
  const [isGoLiveChecksIntroOpen, setIsGoLiveChecksIntroOpen] = useState(false);
  const closeGoLiveChecksIntro = useCallback(() => setIsGoLiveChecksIntroOpen(false), []);
  const handlePublishWebsite = useCallback(async () => {
    // WebsiteV2: publishing lives in the editor header (site-level state on carbon), not on the
    // charm `websiteState`. Hard nav — the admin app is a different bundle.
    if (websiteV2Gate.isWebsiteV2) {
      withWindow(window => window.location.assign(`/${eventHandle}/edit/website`));
      return;
    }
    if (isGoLiveChecksLoading) return;
    if (isGoLiveChecksOnSurface) {
      setIsGoLiveChecksIntroOpen(true);
      return;
    }
    // Gate publishing behind email verification (parity with PublishSection / WebsiteVisibility)
    if (!(await ensureEmailVerified())) {
      return;
    }
    try {
      await websitePublishCapabilityFeature.eventPublishedState.update(true);

      if (event) {
        telemetry.websiteVisibilityPublish(event.id);
      }
      // Show the stationery cross-sell if eligible; otherwise the standard toast.
      if (!handlePublishSuccess()) {
        toast(tq`🥳 Your website has been successfully published!`);
      }
    } catch (error) {
      addError(error, { context: 'GuestSiteController:handlePublishWebsite' });
      toast(tq`😬 Failed to publish website. Please try again.`);
    }
  }, [
    websitePublishCapabilityFeature.eventPublishedState,
    handlePublishSuccess,
    toast,
    tq,
    event,
    telemetry,
    ensureEmailVerified,
    websiteV2Gate.isWebsiteV2,
    eventHandle,
    isGoLiveChecksOnSurface,
    isGoLiveChecksLoading,
  ]);

  useReloadForLegacyGuestsite(event?.featureFlags, event?.website, event?.useLegacyGuestSite);
  const justFonts = useQueryParamHelper().getValue('justFonts');
  const disableActionBar = useProFeature(EventFeature.disableWebActionBar);
  const isBrannan = layout === 'brannan';
  // if isNativeBrannanOverridePage is true, we are rendering the brannan classic layout
  const isActuallyRenderingBrannanClassic = isBrannan && (event?.eventDesign?.brannanNavigation === BrannanNavigation.classic || isNativeBrannanOverridePage);
  const graphicAccent = isBrannan ? (event?.eventDesign?.accent ?? 'default') : undefined;
  const shouldRenderActionBar = (isActuallyRenderingBrannanClassic || isPastFirstPage) && !isAdmin && !isPreviewing && !disableActionBar;

  // loadingGuestSite compound removed — font/theme loading no longer blocks rendering.
  // Fonts are preloaded, theme assets are optimized, and the JoyLogoLoader gate is removed.

  // Skip the synthetic "Get the app" page when locked out, so it doesn't become the only renderable page.
  const hasPasswordProtectedPage = (event?.pagesInfo ?? []).some(p => p.visibility === EventPageVisibility.passwordProtected);
  // Indeterminate unlock state (query in flight) is not "locked out" — avoids a
  // transient "Get the app" page toggle while the unlock query resolves.
  const isLockedOut =
    !isUnlockStateLoading &&
    isGuestLockedOut({
      hasAccess: hasUnlocked,
      pageCount: event?.pages?.length ?? 0,
      hasPasswordProtectedPage,
    });
  const newPages = useMemo(() => (isLockedOut ? event?.pages : addAppPage(event?.website, event)), [addAppPage, event, isLockedOut]);
  const customPages = usePagesWithPhotoAlignment(newPages, event?.pageContainers);
  const { pages: guestSitePages } = useGetGuestSitePages({ pages: customPages, layoutType: layout });

  //Filtering the hidden pages for guest, but display hidden page when it in Admin
  const eventWithVisiblePages = useMemo(() => {
    const eventData = event
      ? {
          ...event,
          pages: guestSitePages as EventPageFragment[],
        }
      : undefined;
    if (eventData && isPreviewing) {
      // Welcome page should always be visible in both website preview and designer preview, even if webiste is hidden
      const visiblePages = eventData.pages.filter(
        page =>
          currentAdminPage === page.type ||
          !page.disabled ||
          page.type === 'welcome' ||
          (page.type === 'custom' && currentAdminPage === page.pageSlug) ||
          (page.type === 'custom' && !page.disabled && (page.pageSlug !== 'accommodations' || !hideAccomPreviewUntilFlagEnabled)),
      );
      return { ...eventData, pages: visiblePages };
    } else if (eventData) {
      return { ...eventData, pages: guestSitePages.length > 0 ? [...guestSitePages] : eventData.pages };
    }
    return undefined;
  }, [event, currentAdminPage, guestSitePages, hideAccomPreviewUntilFlagEnabled]);

  // Register website banners (displayed in priority order)
  useUnpublishedWebsiteBanner({
    isVisible: showUnpublishedBanner,
    onPublish: handlePublishWebsite,
    // Busy while the Go Live Checks flags load, so an early click can't publish past the wizard.
    isPublishing: isWebsitePublishedUpdating || isGoLiveChecksLoading,
  });

  useAccommodationsVisibilityBannerDisplay({
    eventHandle,
    isVisible: showAccommodationsVisibilityBanner,
    onClose: handleCloseAccommodationsVisibilityBanner,
  });

  useChangesReminderBanner({
    eventHandle,
    isVisible: showBannerReminderChanges,
    onClickLink: onClickReminder,
    onClose: onDismissReminder,
  });

  useInAppBrowserBanner({ eventId: event?.id });

  useSmartAppBanner({ isAdmin, telemetryCategory: 'wedding' });

  return {
    event: eventWithVisiblePages,
    layout,
    hasUnpublishedChanges,
    isAdmin,
    isPreviewing,
    handleOnPreviewUpdate,
    handleOnRefetchData,
    eventPageRoutes,
    justFonts,
    graphicAccent,
    shouldRenderActionBar,
    // Used by GuestSiteFeatureSet to gate rendering when full SSR is disabled (CSR/preview path).
    // Fonts and theme aren't preloaded in preview mode, so we need the loading gate.
    loadingGuestSite: loading || loadingFontPackData || loadingThemeData || (styledApplicatorLoading ? loadingTheme : false),
    loadingTheme,
    showBannerReminderChanges,
    setLoadingTheme,
    isPastFirstPage,
    setIsPastFirstPage,
    onDismissReminder,
    onClickReminder,
    showAnnouncement,
    onAnnouncementDismiss,
    currentAdminPage,
    isNativeAccommodationPage,
    isNativeBrannanOverridePage,
    isLoadingWebsitePublishCapability,
    showUnpublishedPage,
    showUnpublishedBanner,
    suppressGuestSiteUntilAuth,
    isPublishingWebsite: isWebsitePublishedUpdating,
    handlePublishWebsite,
    emailVerificationDialogProps,
    crossSellDialogProps,
    isGoLiveChecksIntroOpen,
    closeGoLiveChecksIntro,
    showAccommodationsVisibilityBanner,
    handleCloseAccommodationsVisibilityBanner,
  } as const;
};
